import React, { useCallback, useMemo } from 'react';
import _isEqual from 'lodash/isEqual';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import { CustomFrequencyEnum } from '@/types/schema';

import { REPEAT_SCHEDULE_CUSTOM_FREQUENCY } from '../../../HabitCreateModal/consts';
import HabitDaysOfWeek from './components/HabitDaysOfWeek';
import HabitFrequencySelector from './components/HabitFrequencySelector';
import HabitStartsOnDate from './components/HabitStartsOnDate';
import HabitWeeksOfMonth from './components/HabitWeeksOfMonth';
import WeeksOfMonthSelector from './components/HabitWeeksOfMonth';
import { HabitFrequencyProps } from './types';

const HabitCustomFrequency = ({
  value,
  onChange,
  isEdit,
  initialValue,
  error,
}: HabitFrequencyProps) => {
  const { custom } = value;
  const { t } = useTranslation();
  const isValueChanged = useMemo(() => !_isEqual(value, initialValue), [value, initialValue]);

  const customFrequency = custom?.frequency;

  const onFrequencyChange: React.ComponentProps<typeof HabitFrequencySelector>['onChange'] =
    useCallback(
      (frequency) => {
        onChange({
          ...value,
          custom: { ...REPEAT_SCHEDULE_CUSTOM_FREQUENCY, frequency },
        });
      },
      [onChange, value],
    );

  const onDaysOfWeekChange: React.ComponentProps<typeof HabitDaysOfWeek>['onChange'] = useCallback(
    (daysOfWeek) => {
      onChange({
        ...value,
        custom: {
          ...(value.custom ?? REPEAT_SCHEDULE_CUSTOM_FREQUENCY),
          daysOfWeek,
        },
      });
    },
    [onChange, value],
  );

  const onWeeksOfMonthChange: React.ComponentProps<typeof WeeksOfMonthSelector>['onChange'] =
    useCallback(
      (weeksOfMonth) => {
        onChange({
          ...value,
          custom: { ...(value.custom ?? REPEAT_SCHEDULE_CUSTOM_FREQUENCY), weeksOfMonth },
        });
      },
      [onChange, value],
    );

  const renderContent = useCallback(() => {
    if (customFrequency === CustomFrequencyEnum.Day) {
      return (
        <Stack alignItems="center" justifyContent="between">
          <HabitFrequencySelector value={custom?.frequency} onChange={onFrequencyChange} />
        </Stack>
      );
    }

    if (customFrequency === CustomFrequencyEnum.Week) {
      return (
        <Stack direction="col" gapY={20}>
          <Stack alignItems="center" justifyContent="start">
            <HabitFrequencySelector value={custom?.frequency} onChange={onFrequencyChange} />
            <Typography className="mx-12" variant="body-short-01">
              {t('On')}
            </Typography>
          </Stack>
          <HabitDaysOfWeek
            error={error?.daysOfWeek}
            value={custom?.daysOfWeek}
            onChange={onDaysOfWeekChange}
          />
        </Stack>
      );
    }

    if (customFrequency === CustomFrequencyEnum.Month) {
      return (
        <Stack direction="col" gapY={20}>
          <Stack alignItems="start" justifyContent="start">
            <HabitFrequencySelector
              className="flex flex-1"
              value={custom?.frequency}
              onChange={onFrequencyChange}
            />
            <Typography className="mx-12 mt-12" variant="body-short-01">
              {t('On')}
            </Typography>
            <HabitWeeksOfMonth
              error={error?.weeksOfMonth}
              value={custom?.weeksOfMonth}
              onChange={onWeeksOfMonthChange}
            />
          </Stack>
          <HabitDaysOfWeek
            error={error?.daysOfWeek}
            value={custom?.daysOfWeek}
            onChange={onDaysOfWeekChange}
          />
        </Stack>
      );
    }

    return null;
  }, [
    custom?.daysOfWeek,
    custom?.frequency,
    custom?.weeksOfMonth,
    customFrequency,
    error?.daysOfWeek,
    error?.weeksOfMonth,
    onDaysOfWeekChange,
    onFrequencyChange,
    onWeeksOfMonthChange,
    t,
  ]);

  return (
    <Stack direction="col" gapY={28}>
      <Stack direction="col" gapY={20}>
        {renderContent()}
        {isEdit && isValueChanged ? (
          <Stack
            alignItems="center"
            className="rounded-8 bg-bg-secondary py-[1.1rem] px-8"
            gapX={6}
            justifyContent="start">
            <InfoOutline fillColorToken="icon-primary" height={16} width={16} />
            <Typography variant="body-short-02">{t('Habits:Editor.FrequencyEditInfo')}</Typography>
          </Stack>
        ) : null}
      </Stack>
      <HabitStartsOnDate value={value} onChange={onChange} />
    </Stack>
  );
};

export default HabitCustomFrequency;
